import { Button, Flex, FormControl, FormErrorMessage, FormLabel, Heading, Input, Stack } from '@chakra-ui/react'
import type { UpdateCompetitionPoolRequest } from '@clsplus/api-types/api-admin'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import type { LoaderFunctionArgs } from 'react-router-dom'
import { useLoaderData, useNavigate, useParams, useRevalidator } from 'react-router-dom'
import * as z from 'zod'

import { competitionPoolDetailsQuery } from '../../../../../api/get-competition-pool-details'
import { updateCompetitionPoolDetails } from '../../../../../api/update-competition-pool-details'

export const loader =
  (queryClient: QueryClient) =>
  async ({ params }: LoaderFunctionArgs) =>
    await queryClient.ensureQueryData(
      competitionPoolDetailsQuery(params.competitionId || '', params.stageId || '', params.poolId || '')
    )

const competitionPoolEditSchema = z.object({
  name: z.string().min(1, { message: 'Please enter a pool name' }),
})

type CompetitionPoolEditFormData = z.infer<typeof competitionPoolEditSchema>

export default function CompetitionPoolEdit() {
  const params = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const revalidator = useRevalidator()
  const initialData = useLoaderData() as Awaited<ReturnType<ReturnType<typeof loader>>>

  const stageId = params.stageId || ''
  const competitionId = params.competitionId || ''
  const poolId = params.poolId || ''

  const { data: poolDetails } = useQuery({
    ...competitionPoolDetailsQuery(competitionId, stageId, poolId),
    initialData,
  })

  const updatePersonMutation = useMutation({
    mutationFn: (formData: UpdateCompetitionPoolRequest) =>
      updateCompetitionPoolDetails(competitionId, stageId, poolId, formData),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['competitions'] }),
        queryClient.invalidateQueries({ queryKey: ['competition', competitionId] }),
        queryClient.invalidateQueries({ queryKey: ['stage', stageId] }),
        queryClient.invalidateQueries({ queryKey: ['pool', poolId] }),
      ])
      revalidator.revalidate()
      navigate(`/competitions/${competitionId}/stage/${stageId}/pool/${poolId}`)
    },
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<CompetitionPoolEditFormData>({
    defaultValues: {
      name: poolDetails.name ?? null,
    },
    resolver: zodResolver(competitionPoolEditSchema),
  })

  const handleSubmitClick = (formData: CompetitionPoolEditFormData) => {
    updatePersonMutation.mutate(formData)
  }

  return (
    <Flex direction="column" w="100%">
      <Heading as="h2" size="lg">
        {poolDetails.name}
      </Heading>
      <Stack
        as="form"
        noValidate
        id="pool-edit"
        spacing={4}
        maxW="container.sm"
        mt={8}
        onSubmit={handleSubmit(handleSubmitClick)}
      >
        <FormControl isInvalid={!!errors.name}>
          <FormLabel>Pool name</FormLabel>
          <Input {...register('name')} />
          {errors.name && <FormErrorMessage>{errors.name.message}</FormErrorMessage>}
        </FormControl>
      </Stack>
      <Stack direction="row" spacing={4} mt={8}>
        <Button
          type="submit"
          form="pool-edit"
          variant="primary"
          isDisabled={!isDirty}
          isLoading={updatePersonMutation.isLoading}
        >
          Save changes
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate(`/competitions/${competitionId}/stage/${stageId}/pool/${poolId}`)}
          isDisabled={updatePersonMutation.isLoading}
        >
          Cancel
        </Button>
      </Stack>
    </Flex>
  )
}
